import { z } from 'zod';
import { uuidSchema, pageCursorSchema } from './common.js';
import { sellerDetailSchema } from './seller.js';
import { buyerDetailSchema } from './buyer.js';

/**
 * Customer org DTOs (RUF-365).
 *
 * CustomerOrg is the unified parent org that may have one or more
 * SellerOrg and/or BuyerOrg child profiles.
 */

export const customerOrganizationTypeSchema = z.enum(['business', 'individual', 'broker']);
export type CustomerOrganizationType = z.infer<typeof customerOrganizationTypeSchema>;

export const customerListItemSchema = z.object({
  customerOrgId: uuidSchema,
  /**
   * NULL for an individual org (customer_org.legal_name, migration 861) —
   * an individual has no business legal entity name. Always non-null for a
   * business org. Render `displayName` for identity, not this field
   * directly — see displayName below.
   */
  legalName: z.string().nullable(),
  shortName: z.string().nullable(),
  /** business (default) or individual (SIL-992). */
  organizationType: customerOrganizationTypeSchema,
  /**
   * Single identity label, always non-null: legalName for a business,
   * "First Last" (or the invited email as a last resort) for an individual.
   * Reuses the GET /api/client/me displayName pattern (SIL-993/994) so ops
   * and the customer portal agree on how an individual's identity renders.
   */
  displayName: z.string(),
  isActive: z.boolean(),
  createdAt: z.string(),
  /** True when the org has at least one live listing. */
  isSeller: z.boolean(),
  /** True when the org has submitted at least one offer. */
  isBuyer: z.boolean(),
});
export type CustomerListItem = z.infer<typeof customerListItemSchema>;

export const customerListResponseSchema = z.object({
  data: z.array(customerListItemSchema),
  nextCursor: pageCursorSchema.nullable(),
});
export type CustomerListResponse = z.infer<typeof customerListResponseSchema>;

export const customerDetailSchema = customerListItemSchema.extend({
  /** Primary SellerOrg profile for this customer, if any. */
  sellerProfile: sellerDetailSchema.nullable(),
  /** Primary BuyerOrg profile for this customer, if any. */
  buyerProfile: buyerDetailSchema.nullable(),
});
export type CustomerDetail = z.infer<typeof customerDetailSchema>;

export const customerDetailResponseSchema = z.object({
  data: customerDetailSchema,
});
export type CustomerDetailResponse = z.infer<typeof customerDetailResponseSchema>;

export const customerCreateSchema = z.object({
  legalName: z.string().min(1),
  shortName: z.string().optional(),
  /** SIL-2173: ops can create a broker org through the same business-shaped path. Individuals use POST /individual. */
  organizationType: z.enum(['business', 'broker']).default('business'),
});
export type CustomerCreate = z.infer<typeof customerCreateSchema>;

export const customerUpdateSchema = customerCreateSchema.omit({ organizationType: true }).partial();
export type CustomerUpdate = z.infer<typeof customerUpdateSchema>;

/**
 * Individual customer invite (SIL-992). Ops provides only an email — the
 * system mints the Cognito user, the individual customer_org, an empty
 * individual_profile, the buyer + seller facet rows, and the sole
 * client_user, all in one call. No legalName/shortName — an individual has
 * no business identity name (customer_org.legal_name is nullable for
 * organization_type = 'individual').
 */
export const individualCustomerInviteSchema = z.object({
  email: z.string().email().toLowerCase(),
});
export type IndividualCustomerInvite = z.infer<typeof individualCustomerInviteSchema>;

export const individualCustomerInviteResponseSchema = z.object({
  data: z.object({
    customerOrgId: uuidSchema,
    organizationType: z.literal('individual'),
    userId: uuidSchema,
    email: z.string(),
    isActive: z.boolean(),
    invitedAt: z.string(),
  }),
});
export type IndividualCustomerInviteResponse = z.infer<typeof individualCustomerInviteResponseSchema>;
